/* global __DEV__ */ // Metro defines it on every platform (web included); jest.setup.js sets it.
import { useEffect, useId } from 'react'
import { activateKeepAwakeAsync, deactivateKeepAwake } from 'expo-keep-awake'

/**
 * ATL-2035 — `useKeepAwake` from expo-keep-awake 14.x, with its two promises handled.
 *
 * The upstream hook (expo-keep-awake/build/index.js) calls `activateKeepAwakeAsync(tag).then(…)`
 * with no rejection handler. On the web that is `navigator.wakeLock.request('screen')`, which a
 * browser refuses whenever it likes — a hidden document (QARASAY-KASSA-1Q1, «Document is
 * hidden»), a permission policy, WebKit — and each refusal became an unhandled rejection in
 * Sentry. Its cleanup then calls `deactivateKeepAwake(tag)` for a tag that never activated, which
 * throws `ERR_KEEP_AWAKE_TAG_INVALID` (QARASAY-KASSA-27C). Neither touches a sale or the books: the
 * screen simply may dim. So the promise is caught HERE, where it is created, instead of matching
 * its text in `ignoreErrors` — a text rule would also hide any other error that happened to share
 * the wording.
 *
 * Behaviour is otherwise the upstream hook's: one tag per mounted component, activate on mount,
 * deactivate on unmount. Deactivation is skipped when activation never succeeded.
 */
export function useKeepAwakeSafely() {
  const tag = useId()
  useEffect(() => {
    let active = false
    let mounted = true
    activateKeepAwakeAsync(tag)
      .then(() => {
        active = true
        // Unmounted while activation was in flight: release it now, nothing else will.
        if (!mounted) deactivateKeepAwake(tag).catch(() => {})
      })
      .catch((error) => {
        if (__DEV__) {
          // eslint-disable-next-line no-console
          console.warn('[keep-awake] the platform refused to keep the screen on', error?.message)
        }
      })
    return () => {
      mounted = false
      if (active) deactivateKeepAwake(tag).catch(() => {})
    }
  }, [tag])
}
